import React, {
  useRef,
  useState,
} from 'react';
import RecurrenceCheckbox from 'pages/Dashboard/pages/Appointments/pages/List/components/RecurrenceCheckbox';
import Popover from 'core/components/Popover';
import RecurrenceFields from 'pages/Dashboard/pages/Appointments/pages/List/components/RecurrenceFields';
import EndsOnConditionFields from 'pages/Dashboard/pages/Appointments/pages/List/components/EndsOnConditionFields';
import Button from 'pages/Dashboard/components/Button';
import {
  useFormContext,
} from 'react-hook-form';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details';
import isNil from 'lodash/isNil';
import {
  useConfirmDialog,
} from 'core/hooks';
import defer from 'lodash/defer';

type Props = {
  onSave: (formData: AppointmentForm) => Promise<void>;
  data?: Partial<AppointmentForm>;
  label?: string;
};

export default function RecurranceControl({
  onSave,
  data,
  label,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const {
    handleSubmit,
    setValue,
    formState: { isValid, errors },
  } = useFormContext();

  const { confirm, closeConfirm } = useConfirmDialog();

  const reset = () => {
    defer(() => {
      setValue('recurrence.interval', data?.recurrence?.interval);
      setValue('recurrence.repeatType', data?.recurrence?.repeatType);
      setValue('recurrence.repeatsOn', data?.recurrence?.repeatsOn);
    });
  };

  return (
    <>
      <div className="row-container" ref={containerRef}>
        <RecurrenceCheckbox
          label="Repeats"
          onChange={(checked) => {
            if (checked) {
              setAnchorEl(checked ? containerRef.current : null);
            } else {
              confirm({
                title: 'Turn off Repeats',
                onConfirm: () => {
                  try {
                    setValue('recurrence.interval', undefined);
                    setValue('recurrence.repeatType', undefined);
                    setValue('recurrence.repeatsOn', undefined);
                    setValue('recurrence.endsOn', undefined);
                    handleSubmit(onSave as any)();
                  } finally {
                    closeConfirm();
                  }
                },
                content: (
                  <p className="mb-4">
                    If you turn off repeats, the
                    {' '}
                    {label}
                    {' '}
                    will no longer occur on a recurring basis. It will be a one time event.
                  </p>
                ),
                cancelText: 'Cancel',
                onClose: () => {
                  reset();
                  closeConfirm();
                },
              });
            }
          }}
        />
      </div>
      {!isNil(anchorEl) && (
        <Popover
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            reset();
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          classes={{
            paper: '!w-[608px] !overflow-y-visible !overflow-x-visible populate-form',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <div className="p-4 flex flex-col">
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-500">Repeats Every</span>
              <RecurrenceFields />
              <EndsOnConditionFields />
            </div>
            <div className="flex flex-row gap-3">
              <Button
                onClick={() => {
                  handleSubmit(onSave as any)();
                  setAnchorEl(null);
                }}
                color="primary"
                variant="contained"
                disabled={!isValid && !isNil(errors)}
              >
                Update
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  reset();
                  setAnchorEl(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Popover>
      )}
    </>
  );
}
